import { store } from "./store";
import { app } from "./app";
import { lang, getBrowserLocale } from "./i18n";
import { appRouter } from "./app/routing/app-router";

const router = appRouter;
app.routes.forEach((route) => router.addRoute(route));

const CLINICAL_ROUTE_PREFIXES = ["/consultations", "/patients"];

export function handleRouteAuthFailure(store, next) {
  store.state.isSplashScreen = false;
  if (!store.state.isAuthenticated) {
    next(false);
    return;
  }
  store.commit("setAuthenticated", false);
  next({ name: "Auth" });
}

export function getHomepageRoute(user) {
  // Strip trailing slash so to.path comparisons work reliably.
  // "/" becomes "" (falsy) — avoids a redirect loop via the static root redirect.
  const homepage = user?.homepage?.replace(/\/$/, "");
  return homepage ? { path: homepage } : null;
}

export function routeGuard(to, from, next) {
  // True only on the app's very first navigation. Cleared here and nowhere else,
  // so it reliably distinguishes a genuine cold load from a later requiresAuth →
  // /auth bounce (which is a second navigation and must not re-trigger the check).
  const isInitialNavigation = store.state.initialNavigationPending;
  store.state.initialNavigationPending = false;

  // TODO: Practitioner portal authentication should be further improved!
  //  for now, we only activate it when NOT in the patient portal routes.
  if (!/^\/patient($|\/)/.test(to.path)) {
    if (!to.meta.requiresAuth && !localStorage.getItem("lang_code")) {
      const detectedLocale = getBrowserLocale();
      lang.setLocale(detectedLocale === "sv" ? "sv" : "en");
    }

    if (to.meta.requiresAuth) {
      localStorage.setItem("prev_ref_uri", from.path);
      localStorage.setItem("ref_uri", to.path);

      axios
        .get("/api/internal/auth/information")
        .then((response) => {
          // If token is removed from datebase but not token from browser
          if (response.name == "Auth") {
            return;
          }

          store.commit("setUser", response.data);
          store.commit("setAuthenticated", true);
          store.commit(
            "setPrimaryCenterName",
            response.data.primaryCenter.name,
          );
          lang.setLocale(response.data.culture);
          store.state.isSplashScreen = false;

          const { permissions } = store.state.user;
          const homepage = getHomepageRoute(store.state.user);

          const fallback = homepage ?? { name: "Evaluation" };

          if (
            !permissions.includes("view_clinical_data") &&
            CLINICAL_ROUTE_PREFIXES.some(
              (p) => to.path === p || to.path.startsWith(p + "/"),
            )
          ) {
            next(fallback);
          } else if (
            to.meta.adminAuth &&
            !permissions.includes("manage_dermicus")
          ) {
            next(fallback);
          } else if (
            to.meta.centerAuth &&
            !permissions.includes("manage_center")
          ) {
            next(fallback);
          } else if (
            to.meta.regionAuth &&
            !permissions.includes("manage_region")
          ) {
            next(fallback);
          } else if (
            to.meta.researchAdminAuth &&
            !permissions.includes("manage_study")
          ) {
            next(fallback);
          } else if (
            to.meta.researchParticipantAuth &&
            !permissions.includes("participate_in_studies")
          ) {
            next(fallback);
          } else if (
            to.meta.platformDependant &&
            !to.meta.platformDependant.includes(store.state.dermicusPlatform)
          ) {
            next(fallback);
          } else if (
            to.redirectedFrom?.path === "/" &&
            homepage &&
            to.path !== homepage.path &&
            !to.path.startsWith(homepage.path + "/")
          ) {
            next(homepage);
          } else {
            next();
          }
        })
        .catch((error) => {
          handleRouteAuthFailure(store, next);
        });
    } else if (to.meta.disallowAuthed) {
      if (store.state.isAuthenticated) {
        next(getHomepageRoute(store.state.user) ?? { name: "Evaluation" });
      } else if (isInitialNavigation) {
        // Cold load straight to /auth — the store isn't hydrated yet, so ask the
        // server. _skipAuthRedirect stops the 401 interceptor from also bouncing
        // to /auth. A valid session resolves with the user → go to their homepage.
        axios
          .get("/api/internal/auth/information", { _skipAuthRedirect: true })
          .then((response) =>
            next(getHomepageRoute(response.data) ?? { name: "Evaluation" }),
          )
          .catch(() => next());
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }
}

router.beforeEach(routeGuard);

export default router;
